import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import type { ReactElement } from "react"
import { FormProvider, useForm } from "react-hook-form"

import CompetitorProductForm from "../forms/CompetitorProductForm"
import { type CompetitorProductFormValues, competitorProductFormSchema } from "../forms/competitorProductSchema"
import useCompetitorProductDialogActions from "../hooks/useCompetitorProductDialogActions"
import { buildDefaultFormValues } from "../utils"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { DOCUMENT_UPLOAD_MIME_TYPES } from "@/components/upload/constants"
import useHandleFileUpload from "@/components/upload/hooks/useHandleFileUpload"
import type { CompetitorProduct } from "@/graphql/codegen/graphql"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"

interface CompetitorProductFormDialogProps {
  onClose: () => void
  onSuccess?: () => void
  data?: CompetitorProduct
}

const defaultFormValues: CompetitorProductFormValues = {
  productName: "",
  chemicalName: "",
  casNumbers: [],
  manufacturer: "",
  distributors: [],
  technicalFile: undefined,
}

const CompetitorProductFormDialog = ({ onClose, onSuccess, data }: CompetitorProductFormDialogProps): ReactElement => {
  const isCreateMode = !data

  const methods = useForm<CompetitorProductFormValues>({
    defaultValues: data ? buildDefaultFormValues(data) : defaultFormValues,
    resolver: zodResolver(competitorProductFormSchema),
    mode: "onChange",
  })

  const { uploadFile, onMarkFileAsNeedRemoval, removeFiles } = useHandleFileUpload({
    referenceTableName: ReferenceTableName.CompetitorProduct,
    allowedFileTypes: DOCUMENT_UPLOAD_MIME_TYPES,
  })

  const { setIsCancelConfirmationOpen, isCancelConfirmationOpen, canSubmit, handleSubmit, handleCancel } =
    useCompetitorProductDialogActions({
      methods,
      onSuccess: async (data: CompetitorProduct) => {
        const technicalFile = methods.getValues("technicalFile")

        if (technicalFile) {
          await uploadFile({
            file: technicalFile,
            referenceId: data.competitorProductId!,
            type: FieldGroupEnum.CompetitorProductTechnicalFile,
          })
        }
        await removeFiles()

        onSuccess?.()
      },
      onClose,
      data,
    })

  const dialogTitle = isCreateMode ? "Add Product" : "Edit Product Information"

  return (
    <Dialog open onClose={handleCancel} maxWidth='sm' fullWidth data-testid='competitor-product-dialog'>
      {isCancelConfirmationOpen ? (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      ) : (
        <>
          <DialogHeader title={dialogTitle} onClose={handleCancel} />
          <DialogContent classes={{ root: "p-10" }}>
            <Typography variant='body1' className='mb-6 font-bold text-gray-900'>
              {isCreateMode ? "Add Competitor Product Details" : "Edit Competitor Product Details"}
            </Typography>
            <FormProvider {...methods}>
              <CompetitorProductForm onMarkFileAsNeedRemoval={onMarkFileAsNeedRemoval} />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              appearance='contained'
              onClick={handleSubmit}
              type='submit'
              disabled={!canSubmit}
              data-testid='submit-button'
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default CompetitorProductFormDialog
